import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { EditorForm } from "./shared/EditorForm";
import { EditorField } from "./shared/EditorField";
import { EditorHeader } from "./EditorHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";

type FinancialTable = 'financial_metrics' | 'revenue_data' | 'financial_projections';

export const FinancialsEditor = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const { toast } = useToast();

  const { data: metrics, refetch: refetchMetrics } = useQuery({
    queryKey: ['financial-metrics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('financial_metrics')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const { data: revenueData, refetch: refetchRevenue } = useQuery({
    queryKey: ['revenue-data'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('revenue_data')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const { data: projections, refetch: refetchProjections } = useQuery({
    queryKey: ['financial-projections'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('financial_projections')
        .select('*')
        .order('year', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  const handleMetricsSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      const data = {
        title: String(formData.get('title')),
        value: String(formData.get('value')),
        change_percentage: parseInt(String(formData.get('change_percentage'))),
        icon: String(formData.get('icon'))
      };

      let error;
      
      if (editingItem?.id) {
        ({ error } = await supabase
          .from('financial_metrics')
          .update(data)
          .eq('id', editingItem.id));
      } else {
        ({ error } = await supabase
          .from('financial_metrics')
          .insert(data));
      }

      if (error) throw error;

      toast({
        title: "Success",
        description: editingItem?.id 
          ? "Financial metric updated successfully"
          : "Financial metric added successfully",
      });
      
      setEditingItem(null);
      refetchMetrics();
      e.currentTarget.reset();
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to save financial metric",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevenueSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      const data = {
        month: String(formData.get('month')),
        revenue: parseFloat(String(formData.get('revenue'))),
        expenses: parseFloat(String(formData.get('expenses'))),
        profit: parseFloat(String(formData.get('profit')))
      };

      let error;
      
      if (editingItem?.id) {
        ({ error } = await supabase
          .from('revenue_data')
          .update(data)
          .eq('id', editingItem.id));
      } else {
        ({ error } = await supabase
          .from('revenue_data')
          .insert(data));
      }

      if (error) throw error;

      toast({
        title: "Success",
        description: editingItem?.id 
          ? "Revenue data updated successfully"
          : "Revenue data added successfully",
      });
      
      setEditingItem(null);
      refetchRevenue();
      e.currentTarget.reset();
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to save revenue data",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProjectionsSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      const data = {
        year: parseInt(String(formData.get('year'))),
        revenue: parseFloat(String(formData.get('revenue'))),
        expenses: parseFloat(String(formData.get('expenses'))),
        profit: parseFloat(String(formData.get('profit'))),
        margin: parseInt(String(formData.get('margin')))
      };

      let error;
      
      if (editingItem?.id) {
        ({ error } = await supabase
          .from('financial_projections')
          .update(data)
          .eq('id', editingItem.id));
      } else {
        ({ error } = await supabase
          .from('financial_projections')
          .insert(data));
      }

      if (error) throw error;

      toast({
        title: "Success",
        description: editingItem?.id 
          ? "Financial projection updated successfully"
          : "Financial projection added successfully",
      });
      
      setEditingItem(null);
      refetchProjections();
      e.currentTarget.reset();
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to save financial projection",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
  };

  const handleCancel = () => {
    setEditingItem(null);
  };

  const handleDelete = async (table: FinancialTable, id: string) => {
    try {
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Record deleted successfully",
      });

      // Refetch the appropriate data
      if (table === 'financial_metrics') refetchMetrics();
      if (table === 'revenue_data') refetchRevenue();
      if (table === 'financial_projections') refetchProjections();
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to delete record",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <EditorHeader 
        title="Financial Data Editor"
        description="Manage key metrics, revenue data, and financial projections"
      />
      
      <Tabs defaultValue="metrics" className="w-full">
        <TabsList>
          <TabsTrigger value="metrics">Key Metrics</TabsTrigger>
          <TabsTrigger value="revenue">Revenue Data</TabsTrigger>
          <TabsTrigger value="projections">Projections</TabsTrigger>
        </TabsList>

        <TabsContent value="metrics">
          <EditorForm 
            onSubmit={handleMetricsSubmit} 
            isSubmitting={isSubmitting}
          >
            <div className="space-y-4">
              <EditorField
                label="Metric Title"
                name="title"
                type="text"
                required
                defaultValue={editingItem?.title}
              />
              <EditorField
                label="Value"
                name="value"
                type="text"
                required
                defaultValue={editingItem?.value}
              />
              <EditorField
                label="Change Percentage"
                name="change_percentage"
                type="number"
                required
                defaultValue={editingItem?.change_percentage}
              />
              <EditorField
                label="Icon"
                name="icon"
                type="text"
                required
                defaultValue={editingItem?.icon}
              />
            </div>
            {editingItem && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancel}
                className="mt-2"
              >
                Cancel Edit
              </Button>
            )}
          </EditorForm>

          <div className="mt-8 space-y-4">
            {metrics?.map((metric) => (
              <Card key={metric.id} className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">{metric.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      Value: {metric.value} | Change: {metric.change_percentage}%
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(metric)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete('financial_metrics', metric.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="revenue">
          <EditorForm 
            onSubmit={handleRevenueSubmit} 
            isSubmitting={isSubmitting}
          >
            <div className="space-y-4">
              <EditorField
                label="Month"
                name="month"
                type="text"
                required
                defaultValue={editingItem?.month}
              />
              <EditorField
                label="Revenue"
                name="revenue"
                type="number"
                required
                defaultValue={editingItem?.revenue}
              />
              <EditorField
                label="Expenses"
                name="expenses"
                type="number"
                required
                defaultValue={editingItem?.expenses}
              />
              <EditorField
                label="Profit"
                name="profit"
                type="number"
                required
                defaultValue={editingItem?.profit}
              />
            </div>
            {editingItem && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancel}
                className="mt-2"
              >
                Cancel Edit
              </Button>
            )}
          </EditorForm>

          <div className="mt-8 space-y-4">
            {revenueData?.map((revenue) => (
              <Card key={revenue.id} className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">{revenue.month}</h3>
                    <p className="text-sm text-muted-foreground">
                      Revenue: ${revenue.revenue.toLocaleString()} | 
                      Expenses: ${revenue.expenses.toLocaleString()} | 
                      Profit: ${revenue.profit.toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(revenue)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete('revenue_data', revenue.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="projections">
          <EditorForm 
            onSubmit={handleProjectionsSubmit} 
            isSubmitting={isSubmitting}
          >
            <div className="space-y-4">
              <EditorField
                label="Year"
                name="year"
                type="number"
                required
                defaultValue={editingItem?.year}
              />
              <EditorField
                label="Revenue"
                name="revenue"
                type="number"
                required
                defaultValue={editingItem?.revenue}
              />
              <EditorField
                label="Expenses"
                name="expenses"
                type="number"
                required
                defaultValue={editingItem?.expenses}
              />
              <EditorField
                label="Profit"
                name="profit"
                type="number"
                required
                defaultValue={editingItem?.profit}
              />
              <EditorField
                label="Margin (%)"
                name="margin"
                type="number"
                required
                defaultValue={editingItem?.margin}
              />
            </div>
            {editingItem && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancel}
                className="mt-2"
              >
                Cancel Edit
              </Button>
            )}
          </EditorForm>

          <div className="mt-8 space-y-4">
            {projections?.map((projection) => (
              <Card key={projection.id} className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">Year {projection.year}</h3>
                    <p className="text-sm text-muted-foreground">
                      Revenue: ${projection.revenue.toLocaleString()} | 
                      Expenses: ${projection.expenses.toLocaleString()} | 
                      Profit: ${projection.profit.toLocaleString()} | 
                      Margin: {projection.margin}%
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(projection)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete('financial_projections', projection.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
